import { useEffect, useMemo, useState } from 'react';

import { company } from '~/company/Company';
import { mainStore } from '~/stores';
import { COOKIE_CONSENT_KEY, getCookieValue, setCookie } from '~/utils/cookies';

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

type ConsentValue = 'accepted' | 'declined' | 'dismissed';

/**
 * Shared logic for the GDPR cookie banner: visibility state, consent persistence
 * and the cookie policy link. Used by both the default and the V3 view components.
 */
export const useCookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  const cookiePolicyLink = useMemo(() => {
    return (
      company.config.links.legals?.cookiePolicy?.link ||
      company.config.links.cookie ||
      '/legals#cookies'
    );
  }, []);

  useEffect(() => {
    if (import.meta.env.SSR) {
      return;
    }

    if (!company.showCookieBanner || mainStore.isRN) {
      return;
    }

    const consentValue = getCookieValue(COOKIE_CONSENT_KEY);
    if (!consentValue) {
      setIsVisible(true);
    }
  }, []);

  const setConsent = (value: ConsentValue) => {
    setCookie(COOKIE_CONSENT_KEY, value, { 'max-age': COOKIE_MAX_AGE });
    setIsVisible(false);
  };

  return { isVisible, setConsent, cookiePolicyLink };
};
